import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { blockchainAPI } from '../services/api';
import './BlockchainCertificate.css';

function BlockchainCertificate() {
  const { recordingId } = useParams();
  const navigate = useNavigate();

  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stamping, setStamping] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    loadCertificate();
  }, [recordingId]);

  const loadCertificate = async () => {
    try {
      setLoading(true);
      const res = await blockchainAPI.getCertificate(recordingId);
      setCert(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setCert(null);
      } else {
        toast.error('Failed to load certificate');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStamp = async () => {
    setStamping(true);
    try {
      const res = await blockchainAPI.stamp(recordingId);
      setCert(res.data);
      toast.success('Certificate created! Bitcoin confirmation takes ~1 hour.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create certificate');
    } finally {
      setStamping(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const url = blockchainAPI.getPdfUrl(recordingId);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `voicemeet-certificate-${recordingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const copyHash = (hash) => {
    navigator.clipboard.writeText(hash).then(() => toast.success('Hash copied!'));
  };

  if (loading) {
    return (
      <div className="bc-page">
        <div className="bc-loading">
          <div className="bc-spinner" />
          <p>Loading certificate…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-page">
      <div className="bc-header">
        <button className="bc-back" onClick={() => navigate('/recordings')}>← Back to Recordings</button>
        <h1 className="bc-title">Blockchain Certificate</h1>
        <p className="bc-subtitle">Tamper-proof proof of authenticity anchored on Bitcoin</p>
      </div>

      {!cert ? (
        <motion.div
          className="bc-empty"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bc-empty-icon">🔗</div>
          <h2>No certificate yet</h2>
          <p>Create a tamper-proof certificate for this recording. The audio file hash is submitted to the Bitcoin blockchain via OpenTimestamps — making any edit detectable forever.</p>
          <button
            className="bc-btn-primary"
            onClick={handleStamp}
            disabled={stamping}
          >
            {stamping ? <><span className="bc-spinner-inline" /> Creating Certificate…</> : '🔗 Create Blockchain Certificate'}
          </button>
        </motion.div>
      ) : (
        <motion.div
          className="bc-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Status badge */}
          <div className={`bc-status-badge ${cert.status === 'confirmed' ? 'bc-status-confirmed' : 'bc-status-pending'}`}>
            {cert.status === 'confirmed'
              ? `✓ Confirmed on Bitcoin${cert.bitcoin_block ? ` · Block #${cert.bitcoin_block.toLocaleString()}` : ''}`
              : '⏳ Pending Bitcoin confirmation (~1 hour)'}
          </div>

          {/* Meeting info */}
          <div className="bc-section">
            <h3 className="bc-section-title">Meeting</h3>
            <p className="bc-value">{cert.meeting_title}</p>
          </div>

          <div className="bc-grid">
            <div className="bc-section">
              <h3 className="bc-section-title">Participants</h3>
              <p className="bc-value">{cert.participants?.join(', ') || '—'}</p>
            </div>
            <div className="bc-section">
              <h3 className="bc-section-title">Duration</h3>
              <p className="bc-value">{cert.duration_seconds ? `${Math.floor(cert.duration_seconds / 60)} min` : '—'}</p>
            </div>
            <div className="bc-section">
              <h3 className="bc-section-title">Issued</h3>
              <p className="bc-value">{new Date(cert.issued_at).toLocaleDateString()}</p>
            </div>
            {cert.confirmed_at && (
              <div className="bc-section">
                <h3 className="bc-section-title">Confirmed</h3>
                <p className="bc-value">{new Date(cert.confirmed_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>

          {/* Hashes */}
          <div className="bc-section">
            <h3 className="bc-section-title">Document Hash (SHA-256)</h3>
            <div className="bc-hash-row">
              <code className="bc-hash">{cert.document_hash}</code>
              <button className="bc-copy-btn" onClick={() => copyHash(cert.document_hash)}>Copy</button>
            </div>
          </div>

          <div className="bc-section">
            <h3 className="bc-section-title">Audio File Hash (SHA-256)</h3>
            <div className="bc-hash-row">
              <code className="bc-hash">{cert.file_hash}</code>
              <button className="bc-copy-btn" onClick={() => copyHash(cert.file_hash)}>Copy</button>
            </div>
            <p className="bc-hash-note">If anyone edits even 1 second of audio, this hash will change and verification will fail.</p>
          </div>

          {/* Actions */}
          <div className="bc-actions">
            <button
              className="bc-btn-primary"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? <><span className="bc-spinner-inline" /> Generating PDF…</> : '📄 Download PDF Certificate'}
            </button>
            <button
              className="bc-btn-secondary"
              onClick={() => window.open(`/verify/${cert.document_hash}`, '_blank')}
            >
              🔍 Verify Publicly
            </button>
          </div>

          {/* Verify instructions */}
          <div className="bc-verify-box">
            <h3 className="bc-section-title">How to Verify Independently</h3>
            <ol className="bc-steps">
              <li>Download the .ots proof file from the PDF certificate</li>
              <li>Upload it at <a href="https://opentimestamps.org" target="_blank" rel="noreferrer">opentimestamps.org</a> to verify Bitcoin anchoring</li>
              <li>Or share this URL: <code className="bc-inline-code">/verify/{cert.document_hash}</code></li>
            </ol>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default BlockchainCertificate;
